import React, { useState } from 'react';

import { useChatInteract } from '@chainlit/react-client';

import { Translator } from '@/components/i18n';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';

import { cn } from '@/lib/utils';
import { useSidebar } from '@/components/ui/sidebar';

import { EditSquare } from '../icons/EditSquare';

type NewChatDialogProps = {
  open: boolean;
  handleClose: () => void;
  handleConfirm: () => void;
};

export const NewChatDialog = ({
  open,
  handleClose,
  handleConfirm
}: NewChatDialogProps) => {
  const handleKeyDown = (event: React.KeyboardEvent) => {
    event.preventDefault();
    if (event.key === 'Enter') {
      handleConfirm();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        id="new-chat-dialog"
        className="sm:max-w-md"
        onKeyDown={handleKeyDown}
      >
        <DialogHeader>
          <DialogTitle>
            <Translator path="navigation.newChat.dialog.title" />
          </DialogTitle>
          <DialogDescription>
            <Translator path="navigation.newChat.dialog.description" />
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={handleClose}>
            <Translator path="common.actions.cancel" />
          </Button>
          <Button variant="default" onClick={handleConfirm} id="confirm">
            <Translator path="common.actions.confirm" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

interface Props extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  navigate?: (to: string) => void;
  onConfirm?: () => void;
  /**
   * When true, use sidebar-specific colors so the icon
   * blends into the left navigation. When false, use the
   * global header/background accent tokens instead.
   */
  useSidebarTheme?: boolean;
}

const NewChatButton = ({
  navigate,
  onConfirm,
  useSidebarTheme = true,
  className,
  ...buttonProps
}: Props) => {
  const [open, setOpen] = useState(false);
  const { clear } = useChatInteract();
  const { state } = useSidebar();

  const isSidebarExpanded = useSidebarTheme && state === 'expanded';

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    } else {
      clear();
      navigate?.('/');
    }
    handleClose();
  };

  return (
    <div className={cn(isSidebarExpanded && 'w-full')}>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size={isSidebarExpanded ? 'lg' : 'icon'}
              id="new-chat-button"
              className={cn(
                isSidebarExpanded
                  ? 'h-10 w-full justify-center gap-2 rounded-md px-2 text-sm [&>svg]:size-4'
                  : 'rounded-full',
                useSidebarTheme
                  // ? 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
                  ? 'bg-sidebar-accent text-sidebar-accent-foreground hover:bg-sidebar-accent/50 hover:text-sidebar-accent-foreground'
                  // : 'text-foreground hover:bg-muted hover:text-foreground',
                  : '',
                className
              )}
              onClick={handleClickOpen}
              {...buttonProps}
            >
              <EditSquare className={cn(isSidebarExpanded ? '!size-4':'!size-6')} />
              {isSidebarExpanded && (
                <span className="truncate">
                  <Translator path="navigation.newChat.dialog.tooltip" />
                </span>
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right" align="center" hidden={isSidebarExpanded}>
            <Translator path="navigation.newChat.dialog.tooltip" />
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <NewChatDialog
        open={open}
        handleClose={handleClose}
        handleConfirm={handleConfirm}
      />
    </div>
  );
};

export default NewChatButton;
